import { getLogger } from '@logtape/logtape';
import { ArrowRight } from 'lucide-react';

import AppointmentSchedulerFooter from '@/components/AppointmentScheduler/AppointmentSchedulerFooter.tsx';
import { AppointmentScheduler } from '@/components/AppointmentScheduler/appointment-scheduler.tsx';
import Heading2 from '@/components/text/Heading2.tsx';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { ModalType } from '@/models/Modal.ts';
import { DailyAppointment, Insurance, Priority } from '@/models/appointment/Appointment.ts';
import {
    AppFormInitialState,
    AppointmentBookingFormValues,
} from '@/models/appointment/AppointmentBookingFormValues.ts';
import { AppointmentFormStep } from '@/models/appointment/BookingFormOptions.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { closeModal, openModal } from '@/services/modalService.tsx';

import { PrefilledFormValues, useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';

import { appointmentToForm } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceByKey } from '@/utils/patientUtils.ts';
import { buildPracticeOptions, filterPractitionersByLocation } from '@/utils/practitioner/bookingFormOptionsUtils.ts';

const logger = getLogger(['util', 'appointment-scheduler']);

/**
 * Rounds current time up to the next 5-minute interval
 * Examples: 12:12 -> 12:15, 12:14 -> 12:15, 12:16 -> 12:20, 12:18 -> 12:20
 */
export const roundTimeUp5Minutes = (): string => {
    const now = dayjs();
    const minutes = now.minute();
    const rounded_minutes = Math.ceil(minutes / 5) * 5;

    if (rounded_minutes === 60) {
        return now.minute(0).add(1, 'hour').second(0).millisecond(0).toISOString();
    }

    return now.minute(rounded_minutes).second(0).millisecond(0).toISOString();
};

type SchedulerMode = 'edit' | 'book' | 'view';

interface BaseParams {
    mode: SchedulerMode;
    onClose?: () => void;
}

interface EditParams extends BaseParams {
    mode: 'edit';
    existing_appointment: DailyAppointment;
    enriched_practitioner_data?: EnrichedPractitionerData;
    patient?: GenericPatient;
    isSingleCollectionEdit?: boolean;
    editingCollectionId?: string;
}

interface BookParams extends BaseParams {
    mode: 'book';
    patient?: GenericPatient;
    prefilled_values?: PrefilledFormValues;
}

interface ViewParams extends BaseParams {
    mode: 'view';
    existing_batch_id: string;
}

type OpenSchedulerParams = EditParams | BookParams | ViewParams;

export const openAppointmentScheduler = (params: OpenSchedulerParams): void => {
    const store = useBookingStore.getState();
    const { selected_customer: customer, selected_location: location } = useConfigStore.getState();
    if (!customer || !location) return;

    switch (params.mode) {
        case 'edit': {
            const {
                existing_appointment,
                enriched_practitioner_data,
                patient,
                isSingleCollectionEdit,
                editingCollectionId,
            } = params;

            const current_patient = patient ?? useBookingStore.getState().current_patient;
            if (!current_patient) {
                logger.error('Missing patient data for appointment', { appointment: existing_appointment });
                return;
            }

            const form_data = appointmentToForm(existing_appointment, enriched_practitioner_data);

            store.openSchedulerEditMode({
                patient: current_patient,
                customer: customer,
                location: location,
                appointment: existing_appointment,
                prefilled_values: { id: existing_appointment.id, ...form_data },
                isSingleCollectionEdit: isSingleCollectionEdit,
                editingCollectionId: editingCollectionId,
            });

            break;
        }

        case 'book': {
            const { patient, prefilled_values } = params;

            const current_patient = patient ?? useBookingStore.getState().current_patient;
            if (!current_patient) {
                logger.error('Missing patient data for booking appointment');
                return;
            }

            store.openSchedulerBookMode({
                customer: customer,
                location: location,
                patient: current_patient,
                prefilled_values: prefilled_values,
            });
            break;
        }

        case 'view': {
            const { existing_batch_id } = params;
            store.openSchedulerViewMode(existing_batch_id);
            break;
        }

        default: {
            logger.warn('Invalid mode passed to openAppointmentScheduler');
            return;
        }
    }

    openAppointmentSchedulerModal(params.onClose);
};

const openAppointmentSchedulerModal = (onClose?: () => void): void => {
    useBookingStore.setState({ is_open: true });

    const handleOnClose = (): void => {
        useBookingStore.getState().resetAllData();
        if (onClose) onClose();
    };

    const handleFooterClose = (): void => {
        closeModal(ModalType.APPOINTMENT_SCHEDULER);
    };

    const current_patient = useBookingStore.getState().current_patient;
    openModal({
        modalId: ModalType.APPOINTMENT_SCHEDULER,
        onClose: handleOnClose,
        title: (
            <div className="flex items-center gap-2">
                <Heading2>
                    {current_patient?.personal.last_name} {current_patient?.personal.first_name}{' '}
                    <ArrowRight className="inline" /> Termindetails eingeben
                </Heading2>
            </div>
        ),
        children: <AppointmentScheduler />,
        footer: <AppointmentSchedulerFooter onClose={handleFooterClose} />,
        size: 'xl',
    });
};

interface InitialAppointFormStateParams {
    pvsProvider: PVSProvider;
    formOptionData: EnrichedPractitionerData;
    /** Prismic key of the selected location — scopes the practices the Betriebsstätte offers. */
    locationKey: string | undefined;
    prefilledValues: Partial<AppointmentBookingFormValues>;
    currentPatient?: GenericPatient;
}

export const generateInitialAppointFormState = ({
    pvsProvider,
    formOptionData,
    locationKey,
    prefilledValues,
    currentPatient,
}: InitialAppointFormStateParams): AppFormInitialState => {
    const initial_values: AppointmentBookingFormValues = {
        pvs_provider: pvsProvider,
        insurance: prefilledValues?.insurance ?? Insurance.PUBLIC,
        practice_doc_cirrus_id: prefilledValues?.practice_doc_cirrus_id ?? '',
        apt_type_id_or_schedule_type_pvs_id: prefilledValues?.apt_type_id_or_schedule_type_pvs_id ?? '',
        calendar_pvs_id: prefilledValues?.calendar_pvs_id ?? '',
        duration: prefilledValues?.duration ?? 0,
        date_time: prefilledValues?.date_time ?? roundTimeUp5Minutes(),
        priority: prefilledValues?.priority ?? Priority.NONE,
        is_offered: true,
        description: prefilledValues.description ?? '',
        info: prefilledValues.info ?? '',
    };

    switch (pvsProvider) {
        case PVSProvider.DATA_CUR: {
            if (formOptionData.practitioners.length === 1) {
                // Data cur only has one calendar per practitioner
                initial_values.calendar_pvs_id = formOptionData.practitioners[0].calendars[0].pvs.id;
            }

            // Set default insurance if only one insurance is available
            if (prefilledValues?.insurance === undefined && currentPatient && currentPatient.insurances.length === 1) {
                initial_values.insurance =
                    getInsuranceByKey(currentPatient.insurances[0].type.toUpperCase()) ?? Insurance.PUBLIC;
            }
            break;
        }
        case PVSProvider.DOC_CIRRUS: {
            // Mirrors the Betriebsstätte dropdown, which renders `all_practices` — counting raw
            // practices instead leaves a single-option dropdown unselected.
            const practice_options = buildPracticeOptions(
                formOptionData.practices ?? [],
                filterPractitionersByLocation(formOptionData.practitioners, locationKey)
            );
            if (practice_options.length === 1) {
                initial_values.practice_doc_cirrus_id = practice_options[0].value;
            }

            // Set default insurance if only one insurance is available
            const initial_practice_id = initial_values.practice_doc_cirrus_id;
            if (prefilledValues?.insurance) {
                // skip
            } else if (currentPatient && currentPatient.insurances.length === 1) {
                initial_values.insurance =
                    getInsuranceByKey(currentPatient.insurances[0].type.toUpperCase()) ?? Insurance.PUBLIC;
            } else if (
                currentPatient &&
                !prefilledValues?.insurance &&
                currentPatient.insurances.length > 1 &&
                initial_practice_id
            ) {
                // If more than 1 insurance is available and a location is selected then we can preselect an insurance from the location
                const insurance_from_location = getInsuranceByKey(
                    currentPatient.insurances.find((i) => i.location_id === initial_practice_id)?.type.toUpperCase()
                );
                if (insurance_from_location) {
                    initial_values.insurance = insurance_from_location;
                } else {
                    initial_values.insurance = Insurance.PUBLIC;
                }
            }
            break;
        }
        default: {
            break;
        }
    }

    const initial_step = determineInitialStep(initial_values, pvsProvider);

    return { values: initial_values, initialStep: initial_step };
};

const determineInitialStep = (
    initial_values: AppointmentBookingFormValues,
    pvs_provider: PVSProvider
): AppointmentFormStep => {
    if (!initial_values?.practice_doc_cirrus_id && pvs_provider === PVSProvider.DOC_CIRRUS) {
        return AppointmentFormStep.PRACTICE;
    }
    if (!initial_values?.insurance) {
        return AppointmentFormStep.INSURANCE;
    }
    if (!initial_values?.calendar_pvs_id) {
        return AppointmentFormStep.PRACTITIONER;
    }
    if (!initial_values?.apt_type_id_or_schedule_type_pvs_id) {
        return AppointmentFormStep.APPOINTMENT_TYPE;
    }
    // Everything is already chosen, so start past the guided steps: both flows end on ADDITIONAL_INFO
    // once a type is picked, and the later sections stay hidden on any earlier step.
    return AppointmentFormStep.ADDITIONAL_INFO;
};
